import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import MultiButton from "../../Component/extra/MultiButton";
import Table from "../../Component/Common/Table";
import Button from "../../Component/Common/Button";
import AddIcon from "@mui/icons-material/Add";
import {
  getCourses,
  deleteCourse,
  togglePublishStatus,
} from "../../store/course/course.action";
import { handleOpenNew } from "../../util/Dialogue";
import { ReactComponent as TrashIcon } from "../../assets/icons/trashIcon.svg";
import { ReactComponent as EditIcon } from "../../assets/icons/EditBtn.svg";
import { ReactComponent as EyeIcon } from "../../assets/icons/EyesIcon.svg";
import { warning } from "../../util/Alert";
import Pagination from "../../Component/Common/Pagination";
import noImageFrom from "../../assets/images/noimage.png";
import ToggleSwitch from "../../Component/Common/ToggleSwitch";
import dayjs from "dayjs";
import DataActions from "../../Component/Common/DataActions";
import CreateCourse from "../../Component/dialogue/CreateCourse";

const Course = ({ setSelectedCourse }) => {
  const dispatch = useDispatch();
  const { profile } = useSelector((state) => state.loggedUser);
  const { dialogueType } = useSelector((state) => state.dialogue);
  const { courseData, totalCourses } = useSelector((state) => state.course);

  const [page, setPage] = useState(1);
  const [size, setSize] = useState(10);
  const [publishType, setPublishType] = useState("");
  const [searchText, setSearchText] = useState("");
  const [debounceSearchText, setDebounceSearchText] = useState("");

  const [selectedCourseFilter, setSelectedCourseFilter] = useState("All");
  const [filteredCourse, setFilteredCourse] = useState([]);

  const [editingCourseData, setEditingCourseData] = useState(null);

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
  };

  const handleRowsPerPage = (value) => {
    setPage(1);
    setSize(value);
  };

  const handleDeleteCourse = (row) => {
    const data = warning();
    data
      .then((res) => {
        if (res) {
          const yes = res.isConfirmed;
          if (yes) {
            const id = row?._id;
            dispatch(deleteCourse(id));
          }
        }
      })
      .catch((err) => console.log(err));
  };

  const handleEditSuccess = () => {
    setEditingCourseData(null);
  };

  const handleTogglePublish = (row) => {
    const id = row?._id;
    dispatch(togglePublishStatus(id));
  };

  const courseMapData = [
    {
      Header: "Unique Id",
      Cell: ({ row }) => <p className="fw-bold mb-0">{row?.uniqueId}</p>,
    },
    {
      Header: "Created By",
      Cell: ({ row }) => (
        <span className="text-capitalize fw-bold">{row?.creator?.name}</span>
      ),
    },
    {
      Header: "Title",
      Cell: ({ row }) => <p className="fw-bold mb-0">{row?.title}</p>,
    },
    {
      Header: "Duration",
      Cell: ({ row }) => (
        <span className="text-capitalize">
          {row?.duration ? `${row?.duration} Min` : "-"}
        </span>
      ),
    },
    {
      Header: "Language",
      Cell: ({ row }) => (
        <div
          className="d-flex text-start flex-wrap gap-1 px-2"
          style={{ maxWidth: "200px" }}
        >
          {row?.language}
        </div>
      ),
    },
    {
      Header: "Thumbnail",
      Cell: ({ row }) => (
        <img
          src={row?.thumbnail}
          alt="Course thumbnail"
          height={50}
          width={50}
          onError={(e) => (e.target.src = noImageFrom)}
        />
      ),
    },
    {
      Header: "Certification",
      Cell: ({ row }) => (
        <span className="text-capitalize">
          {row.enableCertification ? "Enabled" : "Disabled"}
        </span>
      ),
    },
    // {
    //   Header: "Price",
    //   Cell: ({ row }) => (
    //     <span className="text-capitalize">
    //       {row.enableCertification
    //         ? `${defaultCurrency?.symbol} ${row?.amount}`
    //         : "-"}
    //     </span>
    //   ),
    // },
    {
      Header: "Created At",
      Cell: ({ row }) => (
        <span className="text-capitalize">
          {row?.createdAt ? dayjs(row?.createdAt).format("DD MMM YYYY") : "-"}
        </span>
      ),
    },
    {
      Header: "Publish",
      Cell: ({ row }) => (
        <ToggleSwitch
          checked={row?.isPublished}
          onChange={() => handleTogglePublish(row)}
        />
      ),
    },
    {
      Header: "Actions",
      Cell: ({ row }) => (
        <div className="d-flex justify-content-center gap-2">
          <Button
            className="icon-btn"
            startIcon={<EyeIcon />}
            onClick={() => setSelectedCourse(row)}
          />
          {row.creatorId === profile?._id && (
            <Button
              className="icon-btn"
              startIcon={<EditIcon />}
              onClick={() => {
                handleOpenNew(dispatch, "createCourse");
                setEditingCourseData(row);
              }}
            />
          )}
          <Button
            className="icon-btn"
            startIcon={<TrashIcon />}
            onClick={() => handleDeleteCourse(row)}
          />
        </div>
      ),
    },
  ];

  useEffect(() => {
    setSelectedCourseFilter("All");
  }, []);

  useEffect(() => {
    // Initialize Redux store with course data
    dispatch(getCourses(page, size, publishType, debounceSearchText));
  }, [dispatch, page, size, publishType, debounceSearchText]);

  // Debouncing for search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setPage(1);
      setDebounceSearchText(searchText);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchText]);

  useEffect(() => {
    setFilteredCourse(courseData);
  }, [courseData]);

  useEffect(() => {
    setPage(1);
    if (selectedCourseFilter === "All") {
      setPublishType("");
    } else if (selectedCourseFilter === "Published") {
      setPublishType("published");
    } else if (selectedCourseFilter === "Draft") {
      setPublishType("unpublished");
    }
  }, [selectedCourseFilter]);

  return (
    <div className="">
      <MultiButton
        label={["All", "Published", "Draft"]}
        multiButtonSelect={selectedCourseFilter}
        setMultiButtonSelect={setSelectedCourseFilter}
      />

      <div className="user-table mb-3">
        <div className="user-table-top">
          <div className="mb-3 d-flex justify-content-end align-items-center">
            <Button
              type="button"
              className={"rounded"}
              startIcon={<AddIcon />}
              onClick={() => handleOpenNew(dispatch, "createCourse")}
            >
              Course
            </Button>
          </div>

          <DataActions
            searchInputProps={{
              placeholder: "Search ID, Title, Creator",
              value: searchText,
              onValueChange: (value) => setSearchText(value),
            }}
          />
        </div>

        <Table
          data={filteredCourse}
          mapData={courseMapData}
          noDataMessage="No Course Found!"
        />

        <div className="mt-3">
          <Pagination
            totalItem={totalCourses}
            activePage={page}
            pageSize={size}
            onActivePageChange={handlePageChange}
            onPageSizeChange={handleRowsPerPage}
          />
        </div>
      </div>

      {dialogueType === "createCourse" && (
        <CreateCourse
          editingCourseData={editingCourseData}
          isEditing={editingCourseData !== null}
          onEditSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default Course;
